import { getVapidKey, subscribePush } from './api/chat';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

// Lần gần nhất TẠO LẠI sub (huỷ + đăng ký mới) — hạn chế churn trên iOS khi app
// liên tục vào/ra foreground. Chỉ tạo lại tối đa 1 lần / khoảng này.
let lastFreshAt = 0;
const FRESH_INTERVAL_MS = 30 * 60 * 1000; // 30 phút

// Đăng ký Service Worker + Web Push, gửi subscription về backend.
export async function registerPush() {
  // App native (Capacitor Android/iOS): WebView KHÔNG có Web Push -> dùng FCM thay thế.
  try {
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor?.isNativePlatform?.()) {
      const { registerNativePush } = await import('./nativePush');
      await registerNativePush();
      return;
    }
  } catch { /* không phải môi trường Capacitor -> tiếp tục Web Push */ }
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
  // Môi trường DEV (localhost): KHÔNG đăng ký push để khỏi trùng thông báo với bản web thật.
  // Nếu trước đó lỡ đăng ký thì gỡ subscription cũ. (Dev vẫn có popup in-app + âm thanh.)
  const isLocal = /^(localhost|127\.0\.0\.1|\[::1\])$/.test(location.hostname);
  if (isLocal) {
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg && (await reg.pushManager.getSubscription());
      if (sub) await sub.unsubscribe();
    } catch { /* ignore */ }
    return;
  }
  try {
    const reg = await navigator.serviceWorker.register('/sw.js');
    if (Notification.permission === 'default') await Notification.requestPermission();
    if (Notification.permission !== 'granted') return;
    const { key } = await getVapidKey();
    if (!key) return;
    const appKey = urlBase64ToUint8Array(key);

    // iOS/iPadOS hay để subscription CHẾT NGẦM: server gửi bị 410 -> xoá,
    // nhưng getSubscription() vẫn trả cái chết đó -> đăng ký lại vẫn chết -> mất thông báo.
    // (Thoát/đăng nhập lại thì được vì lúc đăng xuất đã huỷ sub cũ.)
    // Cách chữa: trên iOS luôn tạo LẠI subscription mới mỗi lần mở app để "tự lành".
    const isIOS =
      /iP(hone|ad|od)/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    let sub = await reg.pushManager.getSubscription();

    // Sub cũ dùng khoá VAPID khác (đổi khoá server) -> phải tạo lại.
    const keyMismatch =
      sub &&
      sub.options &&
      sub.options.applicationServerKey &&
      !sameKey(sub.options.applicationServerKey, appKey);

    // Tạo lại sub trên iOS để tự lành sub chết ngầm, nhưng CÓ HẠN CHẾ tần suất
    // (tối đa 1 lần / FRESH_INTERVAL) để khỏi churn khi app vào/ra foreground liên tục.
    const now = Date.now();
    const iosStale = isIOS && now - lastFreshAt > FRESH_INTERVAL_MS;

    if (sub && (iosStale || keyMismatch)) {
      try { await sub.unsubscribe(); } catch { /* ignore */ }
      sub = null;
    }
    if (!sub) {
      sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: appKey });
      lastFreshAt = now;
    }
    await subscribePush(sub);
  } catch (err) {
    console.warn('Đăng ký push thất bại:', err?.message || err);
  }
}

// Tự làm mới subscription khi app QUAY LẠI foreground (mở lại từ nền, chuyển tab về).
// Rút ngắn khoảng "lọt tin" trên iPhone. Gọi 1 lần (idempotent); có tiết lưu (throttle).
let refreshInstalled = false;
let lastRunAt = 0;
const MIN_RUN_MS = 5 * 60 * 1000; // tối thiểu 5 phút giữa 2 lần chạy do foreground
export function installPushRefresh() {
  if (refreshInstalled) return;
  refreshInstalled = true;
  const onWake = () => {
    if (document.visibilityState !== 'visible') return;
    const now = Date.now();
    if (now - lastRunAt < MIN_RUN_MS) return;
    lastRunAt = now;
    registerPush();
  };
  document.addEventListener('visibilitychange', onWake, { passive: true });
  window.addEventListener('focus', onWake, { passive: true });
}

// So sánh applicationServerKey (ArrayBuffer) với khoá hiện tại (Uint8Array).
function sameKey(a, b) {
  const x = new Uint8Array(a);
  if (x.length !== b.length) return false;
  for (let i = 0; i < b.length; i++) if (x[i] !== b[i]) return false;
  return true;
}
